import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
// material
import { styled } from '@mui/material/styles';
import { Box, Button, Container, Typography, TextField, Alert } from '@mui/material';
// layouts
import LogoOnlyLayout from '../../layouts/LogoOnlyLayout';
// routes
import { PATH_AUTH } from '../../routes/paths';
// components
import Page from '../../components/Page';
import * as Yup from 'yup';
import { Form, FormikProvider, useFormik } from 'formik';
import { LoadingButton } from '@mui/lab';
import { RootState, useSelector } from './../../redux/store';
import axios from '../../utils/axios';
import { useSnackbar } from 'notistack';
import closeFill from '@iconify/icons-eva/close-fill';
import { Icon } from '@iconify/react';
import MIconButton from 'components/@material-extend/MIconButton';
import { useTranslation } from 'react-i18next';

// ----------------------------------------------------------------------

const RootStyle = styled(Page)(({ theme }) => ({
  display: 'flex',
  minHeight: '100%',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(12, 0)
}));

type InitialValues = {
  password: string;
  passwordConfirmation: string;
};

const HeaderStyle = styled('header')(({ theme }) => ({
  top: 0,
  left: 0,
  lineHeight: 0,
  width: '100%',
  position: 'absolute',
  padding: theme.spacing(3, 3, 0),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(5, 5, 0)
  }
}));

// ----------------------------------------------------------------------

export default function ResetPassword() {
  const [error, setError] = useState(false);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const { workspace } = useSelector((state: RootState) => state.workspace);
  const { guid } = useParams();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const OnboardingSchema = Yup.object().shape({
    password: Yup.string()
      .required('Indica una contraseña')
      .min(8, 'Debe tener 8 carácteres o más')
      .max(50, 'La contraseña no debe tener más de 20 carácteres')
      .matches(/^(?=.*[a-z])/, 'La contraseña debe contener al menos una minúscula')
      .matches(/^(?=.*[A-Z])/, 'La contraseña debe contener al menos una mayúscula')
      .matches(/^(?=.*[0-9])/, 'La contraseña debe contener al menos un número')
      .matches(/^(?=.*[!@#%&])/, 'La contraseña debe contener al menos un carácter especial'),
    passwordConfirmation: Yup.string()
      .required('Repite la contraseña')
      .oneOf([Yup.ref('password'), null], 'Las contraseñas deben ser iguales')
  });

  const formik = useFormik<InitialValues>({
    initialValues: {
      password: '',
      passwordConfirmation: ''
    },
    validationSchema: OnboardingSchema,
    onSubmit: async (values) => {
      setLoading(true);
      axios
        .post('/open/user-invitation/' + guid, { password: values.password })
        .then((data) => {
          enqueueSnackbar(t('password_set_correctly'), {
            variant: 'success',
            action: (key) => (
              <MIconButton size="small" onClick={() => closeSnackbar(key)}>
                <Icon icon={closeFill} />
              </MIconButton>
            )
          });
          setLoading(false);
          setTimeout(() => {
            navigate('/');
          }, 3000);
        })
        .catch((e) => {
          enqueueSnackbar(e.detail ?? t('error'), {
            variant: 'error',
            action: (key) => (
              <MIconButton size="small" onClick={() => closeSnackbar(key)}>
                <Icon icon={closeFill} />
              </MIconButton>
            )
          });
          setLoading(false);
          setTimeout(() => {
            navigate('/');
          }, 3000);
        });
    }
  });
  const { errors, touched, handleSubmit, getFieldProps } = formik;

  useEffect(() => {
    const getInvitation = () => {
      axios
        .get('/open/user-invitation/' + guid)
        .then(() => {
          setError(false);
          setReady(true);
        })
        .catch((e) => {
          setError(true);
          setReady(false);
        });
    };
    getInvitation();
  }, [guid]);

  return (
    <RootStyle title="Onboarding">
      {workspace?.wsLogo ? (
        <HeaderStyle>
          <RouterLink to="/">
            <Box sx={{ width: 144, height: 40 }}>
              <img src={workspace.wsLogo} alt="login" />
            </Box>
          </RouterLink>
        </HeaderStyle>
      ) : (
        <LogoOnlyLayout />
      )}
      <Container>
        <Box sx={{ maxWidth: 480, mx: 'auto' }}>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <Typography variant="h3" paragraph>
                {t('welcome_to')} {workspace?.wsFullName}
              </Typography>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {t('link_incorrect_or_timed_out')}.
                </Alert>
              )}

              <Typography sx={{ color: 'text.secondary', mb: 5 }}>
                {t('to_access_panel_set_password')}.
              </Typography>
              <TextField
                fullWidth
                {...getFieldProps('password')}
                type="password"
                label="Contraseña"
                error={Boolean(touched.password && errors.password)}
                helperText={touched.password && errors.password}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                {...getFieldProps('passwordConfirmation')}
                type="password"
                label="Repita la contraseña"
                error={Boolean(touched.passwordConfirmation && errors.passwordConfirmation)}
                helperText={touched.passwordConfirmation && errors.passwordConfirmation}
              />

              <LoadingButton
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                loading={loading}
                sx={{ my: 2 }}
                disabled={!ready}
              >
                {t('set_password')}
              </LoadingButton>
            </Form>

            <Button
              fullWidth
              size="large"
              component={RouterLink}
              to={PATH_AUTH.login}
              sx={{ mt: 1 }}
            >
              {t('return')}
            </Button>
          </FormikProvider>
        </Box>
      </Container>
    </RootStyle>
  );
}
